import axios from 'axios';
import { browserHistory } from 'react-router';

import { resetIntercom } from './intercom';
import { refreshToken } from './ServiceManager';
import { setSessionStorageItem } from './sessionStorage';

export default function tokenExpiredOrMaintenanceOrForbidden() {
  const unhautorizedErrorStatus = 401;
  const forbiddenErrorStatus = 403;
  const maintenanceErrorStatus = 503;
  const errorMessages = [
    'Session expired, please log in again',
    'No admin found',
    'No session found',
    'Maintenance',
    'Session invalidated',
  ];

  axios.defaults.withCredentials = true;

  // --- Concurrent refresh control ---
  let isRefreshing = false;
  let pendingRequests = [];

  axios.interceptors.request.use((config) => {
    if (!config.headers['app-type']) {
      config.headers['app-type'] = 'admin';
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!error.response) return Promise.reject(error);

      const currentRoute = browserHistory.getCurrentLocation().pathname;
      const { status, data, config: originalRequest } = error.response;

      // Validate if the error is in the list of controlled errors
      if (
        typeof data === 'object' &&
        'err' in data &&
        errorMessages.includes(data.err)
      ) {
        // --- CASE 401: expired token ---
        if (status === unhautorizedErrorStatus) {
          if (data.err === 'Session expired, please log in again') {
            // Try refresh and then retry the request
            if (isRefreshing) {
              // There's already a refresh in progress → add this request to the queue
              return new Promise((resolve) => {
                pendingRequests.push(() => resolve(axios(originalRequest)));
              });
            }
            isRefreshing = true;
            try {
              await refreshToken(); // new cookies set in backend
              // Retry the current request
              const result = await axios(originalRequest);
              // Retry all queued requests
              pendingRequests.forEach((cb) => cb());
              pendingRequests = [];
              isRefreshing = false;
              return result;
            } catch (err) {
              isRefreshing = false;
              pendingRequests = [];
              // Refresh failed -> send to login
              setSessionStorageItem(
                'url_before_redirected_to_login',
                currentRoute,
              );
              localStorage.clear();
              resetIntercom();
              browserHistory.replace('/login');
              return new Promise(() => {});
            }
          }
          // Other 401 cases that are not expired token
          setSessionStorageItem('url_before_redirected_to_login', currentRoute);
          localStorage.clear();
          resetIntercom();
          browserHistory.replace('/login');
          return new Promise(() => {});
        }

        // --- CASE 403: forbidden ---
        if (status === forbiddenErrorStatus) {
          browserHistory.replace('/401');
          return new Promise(() => {});
        }

        // --- CASE 503: maintenance ---
        if (status === maintenanceErrorStatus) {
          browserHistory.replace('/maintenance');
          return new Promise(() => {});
        }
      }

      return Promise.reject(error);
    },
  );
}
